import React, { useMemo, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Stack,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FaSearch, FaFilter } from 'react-icons/fa';
import { appTypographySx, getCommonStyles, getAppTextPrimary } from '../../theme';
import { MONO, todayTokens } from '../../theme/todayTokens';

const GlobalSearchModal = ({
  open,
  onClose,
  modalRef,
  isDarkMode,
  primaryColor,
  searchQuery,
  onSearchInputChange,
  searchInputRef,
  showFilters,
  onToggleFilters,
  selectedCategories,
  onToggleCategory,
  onClearCategories,
  availableCategories,
  searchResults,
  isSearching,
  onSelectResult,
  anchored = false,
  onOpen,
}) => {
  const pageTextPrimary = getAppTextPrimary(isDarkMode);
  const commonStyles = getCommonStyles(isDarkMode);
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const tk = todayTokens(isDarkMode);

  // Lock body scroll while the full-screen modal is open (mobile)
  useEffect(() => {
    if (!open || anchored) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, anchored]);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  const modalPaperSx = useMemo(
    () => ({
      width: '100%',
      maxWidth: { xs: '100%', sm: 720 },
      height: { xs: '100%', sm: 'auto' },
      maxHeight: { xs: '100%', sm: 'calc(100vh - 6rem)' },
      borderRadius: { xs: 0, sm: '16px' },
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: isDarkMode ? '#1a1a1a' : '#fff',
      color: isDarkMode ? '#fff' : 'inherit',
      border: '1px solid',
      borderColor,
      boxShadow: {
        xs: 'none',
        sm: isDarkMode
          ? '0 24px 48px rgba(0,0,0,0.45)'
          : '0 24px 48px rgba(0,0,0,0.12)',
      },
      ...commonStyles,
    }),
    [isDarkMode, borderColor, commonStyles]
  );

  const inputFieldSx = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '12px',
      bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& .topbar-search-modal__input::placeholder': {
        color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
        opacity: 1,
      },
    }),
    [isDarkMode]
  );

  const inputStyle = useMemo(
    () => ({
      boxSizing: 'border-box',
      height: 56,
      width: '100%',
      paddingLeft: 44,
      paddingRight: 16,
      border: 'none',
      outline: 'none',
      borderRadius: '12px',
      fontSize: 16,
      fontWeight: 500,
      background: 'transparent',
      color: isDarkMode ? '#fff' : '#111827',
    }),
    [isDarkMode]
  );

  const categoryLabels = {
    recent: 'Recent searches',
    pages: 'Pages',
    properties: 'Properties',
    tenants: 'Tenants',
    maintenance: 'Maintenance',
    landlords: 'Landlords',
    finance: 'Finance',
    payments: 'Payments',
    documents: 'Documents',
    certificates: 'Certificates',
    calendar: 'Calendar',
    users: 'Users',
    links: 'Links',
    settings: 'Settings',
    error: 'Error',
  };

  const showResults = searchResults.length > 0;
  const showHint = (!searchQuery || searchQuery.length < 2) && !showResults && !isSearching;
  const showEmpty =
    searchQuery && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching;

  const mutedText = isDarkMode ? 'rgba(255,255,255,0.55)' : 'text.secondary';
  const optionActiveBg = anchored
    ? tk.surfaceAlt
    : (isDarkMode ? `${primaryColor}22` : `${primaryColor}12`);

  const filtersBlock = showFilters && (
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor, flexShrink: 0 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: mutedText }}>
        Filter by category
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
        {availableCategories.map((category) => {
          const isSelected = selectedCategories.includes(category.id);
          const IconComponent =
            typeof category.icon === 'function' ? category.icon : FaSearch;
          return (
            <Chip
              key={category.id}
              size="small"
              icon={<IconComponent style={{ fontSize: 10 }} />}
              label={category.name}
              onClick={() => onToggleCategory(category.id)}
              sx={{
                height: 26,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: isSelected
                  ? (isDarkMode ? `${primaryColor}33` : `${primaryColor}18`)
                  : isDarkMode
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)',
                color: isSelected ? primaryColor : 'inherit',
                border: '1px solid',
                borderColor: isSelected ? primaryColor : borderColor,
              }}
            />
          );
        })}
        {selectedCategories.length > 0 && (
          <Chip
            size="small"
            label="Clear"
            onClick={onClearCategories}
            sx={{ height: 26, fontSize: '0.7rem' }}
          />
        )}
      </Stack>
    </Box>
  );

  const resultsBlock = (
    <>
      {showResults && (
        <Combobox.Options
          static
          style={{
            flex: 1,
            maxHeight: 'none',
            overflowY: 'auto',
            margin: 0,
            padding: '8px 12px 16px',
            listStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {searchResults.map((category) => (
            <Box key={category.category} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1,
                  py: 0.75,
                  mb: 0.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'text.secondary',
                }}
              >
                {categoryLabels[category.category] || category.category}
              </Typography>
              <Stack spacing={0.5}>
                {category.items.map((item) => (
                  <Combobox.Option
                    key={item.id}
                    value={{ ...item, category: category.category }}
                    className="topbar-search-modal__option"
                  >
                    {({ active }) => (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          bgcolor: active ? optionActiveBg : 'transparent',
                          border: '1px solid',
                          borderColor: active && !anchored ? primaryColor : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: active && !anchored ? primaryColor : 'inherit',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.title}
                        </Typography>
                        {item.subtitle && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.25,
                              color: mutedText,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.subtitle}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Combobox.Option>
                ))}
              </Stack>
            </Box>
          ))}
        </Combobox.Options>
      )}

      {isSearching && !showResults && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={28} sx={{ color: primaryColor }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Searching…
          </Typography>
        </Stack>
      )}

      {showHint && !isSearching && (
        <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Start typing to search
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.5, color: mutedText }}>
            Properties, tenants, maintenance, and more
          </Typography>
        </Box>
      )}

      {showEmpty && (
        <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            No results found
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.5, color: mutedText }}>
            No results for &ldquo;{searchQuery}&rdquo;. Try another term or adjust filters.
          </Typography>
        </Box>
      )}
    </>
  );

  if (anchored) {
    return (
      <Box
        ref={modalRef}
        className="topbar-search-anchor"
        sx={{ position: 'relative', flexGrow: 1, maxWidth: 560, minWidth: 0 }}
      >
        <Combobox value={searchQuery} onChange={onSelectResult}>
          <div className="today-header__search">
            <FaSearch size={15} aria-hidden="true" />
            <Combobox.Input
              ref={searchInputRef}
              className="today-header__search-input"
              placeholder="Search a property, person, postcode or job ref…"
              aria-label="Search"
              autoComplete="off"
              onChange={onSearchInputChange}
              onFocus={onOpen}
              onClick={onOpen}
            />
            {isSearching ? (
              <CircularProgress size={14} sx={{ color: tk.navy, flexShrink: 0 }} />
            ) : (
              <kbd style={{ fontFamily: MONO }}>Ctrl K</kbd>
            )}
          </div>

          {open && (
            <Paper
              elevation={0}
              className="topbar-search-modal__panel"
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: '100%',
                minWidth: 420,
                maxHeight: 'min(560px, calc(100vh - 120px))',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 60,
                borderRadius: '16px',
                bgcolor: tk.surface,
                color: tk.ink,
                border: `1px solid ${tk.line}`,
                boxShadow: isDarkMode
                  ? '0 18px 40px rgba(0,0,0,0.45)'
                  : '0 18px 40px rgba(21,23,28,0.12)',
                ...appTypographySx(isDarkMode),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderBottom: `1px solid ${tk.lineSoft}`,
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" sx={{ color: tk.muted }}>
                  {searchQuery && searchQuery.length >= 2 ? 'Results' : 'Search'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onToggleFilters}
                  aria-label="Toggle search filters"
                  aria-pressed={showFilters}
                  sx={{ color: showFilters ? tk.navy : tk.muted }}
                >
                  <FaFilter size={12} />
                </IconButton>
              </Box>
              {filtersBlock}
              {resultsBlock}
            </Paper>
          )}
        </Combobox>
      </Box>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="topbar-search-modal"
          style={{ position: 'fixed', inset: 0, zIndex: 99999 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
        >
          <Box
            onClick={onClose}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: isDarkMode ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.35)',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              px: { xs: 0, sm: 3 },
              pt: { xs: 0, sm: 12 },
              pointerEvents: 'none',
              color: pageTextPrimary,
              ...appTypographySx(isDarkMode),
            }}
          >
            <motion.div
              ref={modalRef}
              style={{ pointerEvents: 'auto', width: '100%', display: 'flex', justifyContent: 'center', height: '100%' }}
              initial={prefersReducedMotion ? false : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
            >
              <Paper elevation={0} sx={modalPaperSx} className="topbar-search-modal__panel">
                <Combobox value={searchQuery} onChange={onSelectResult}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor, flexShrink: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                        Search
                      </Typography>
                      {isSearching && (
                        <CircularProgress size={16} sx={{ color: primaryColor }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'text.secondary',
                          display: { xs: 'none', sm: 'inline' },
                        }}
                      >
                        Ctrl+K
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={onToggleFilters}
                        aria-label="Toggle search filters"
                        sx={{
                          color: showFilters ? primaryColor : isDarkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                        }}
                      >
                        <FaFilter size={14} />
                      </IconButton>
                      <IconButton size="small" onClick={onClose} aria-label="Close search">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Box sx={inputFieldSx} className="topbar-search-modal__input-wrap">
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          left: 14,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          pointerEvents: 'none',
                          zIndex: 1,
                          color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)',
                        }}
                      >
                        <FaSearch size={16} />
                      </Box>
                      <Combobox.Input
                        className="topbar-search-modal__input"
                        style={inputStyle}
                        placeholder="Search properties, tenants, maintenance…"
                        onChange={onSearchInputChange}
                        ref={searchInputRef}
                      />
                    </Box>
                  </Box>

                  {filtersBlock}
                  {resultsBlock}
                </Combobox>
              </Paper>
            </motion.div>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearchModal;
